import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { useGetFishTypes } from '@/domain/cages';
import type { FishGroupDetail } from '@/domain/fishGroups';
import { translateDataLabel } from '@/i18n/dataLabels';
import { formatDateTimeWorded } from '@/utils/date';
import { formatNumberMaxDecimalsByLocale } from '@/utils/localeFormatters';

import EditFishGroupMetadataModal from '../../actions/EditFishGroupMetadataModal';

import styles from '../FishGroupDetailModal.module.scss';

interface Props {
    clientId: number;
    detail: FishGroupDetail;
}

const PLACEHOLDER = '—';

export default function MetadataTab(props: Props) {
    const { clientId, detail } = props;
    const { t, i18n } = useTranslation('fishGroups');
    // Species names are stored data, translated under the default namespace.
    const { t: tLegacy } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const fishTypesQuery = useGetFishTypes();
    const fishType = fishTypesQuery.data.find((type) => type.id === detail.fishTypeId);
    // The id stands in only for a species the list does not know, never while it loads.
    let fishTypeLabel: string | null = null;
    if (fishType) fishTypeLabel = translateDataLabel(tLegacy, 'fishTypes', fishType.name);
    else if (!fishTypesQuery.isLoading) fishTypeLabel = String(detail.fishTypeId);

    const rows: Array<{ label: string; value: string | null }> = [
        { label: t('detail.metadata.id'), value: String(detail.id) },
        { label: t('detail.metadata.name'), value: detail.name },
        { label: t('detail.metadata.status'), value: detail.status },
        { label: t('detail.metadata.fishType'), value: fishTypeLabel },
        {
            label: t('detail.metadata.initialPopulationSize'),
            value:
                detail.initialPopulationSize == null
                    ? null
                    : formatNumberMaxDecimalsByLocale(detail.initialPopulationSize, i18n.language),
        },
        {
            label: t('detail.metadata.lifecycleStartedAt'),
            value: detail.lifecycleStartedAt
                ? formatDateTimeWorded(detail.lifecycleStartedAt, 'minute')
                : null,
        },
        {
            label: t('detail.metadata.lifecycleEndedAt'),
            value: detail.lifecycleEndedAt
                ? formatDateTimeWorded(detail.lifecycleEndedAt, 'minute')
                : null,
        },
        {
            label: t('detail.metadata.currentLocation'),
            value: detail.currentLocation
                ? `${detail.currentLocation.cageName} · ${detail.currentLocation.locationName} (${t(
                      'detail.metadata.since',
                      { date: formatDateTimeWorded(detail.currentLocation.fromDate, 'minute') }
                  )})`
                : null,
        },
        { label: t('detail.metadata.notes'), value: detail.notes },
    ];

    return (
        <>
            <div className={styles.metadataHeader}>
                {/* Deliberately not in FishGroupActionsBar: that component hides
                    itself for non-active groups, and correcting a typo on a
                    closed group has to work. */}
                <Button
                    variant="secondary"
                    size="small"
                    type="button"
                    onClick={() => setIsEditing(true)}>
                    {t('detail.metadata.editButton')}
                </Button>
            </div>
            <dl className={styles.metadataGrid}>
                {rows.map((row) => (
                    <RowEntry key={row.label} label={row.label} value={row.value} />
                ))}
            </dl>
            <EditFishGroupMetadataModal
                clientId={clientId}
                detail={detail}
                isOpen={isEditing}
                onClose={() => setIsEditing(false)}
            />
        </>
    );
}

interface RowEntryProps {
    label: string;
    value: string | null;
}

function RowEntry(props: RowEntryProps) {
    const { label, value } = props;
    return (
        <>
            <dt className={styles.metadataLabel}>{label}</dt>
            <dd
                className={`${styles.metadataValue} ${
                    value == null ? styles['metadataValue--placeholder'] : ''
                }`}>
                {value ?? PLACEHOLDER}
            </dd>
        </>
    );
}
